import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CloudCog, Languages, LoaderCircle, MessagesSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import ThemeSwitcher from '../components/ThemeSwitcher';

export default function RegisterPage() {
  const [form, setForm] = useState({ username: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [botUsername, setBotUsername] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? i18n.language;

  useEffect(() => {
    api.getPublicSettings().then((settings) => {
      if (settings?.max_bot_username) setBotUsername(settings.max_bot_username);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.password !== form.confirm) { setError(t('register.passwordMismatch')); return; }
    setError('');
    setLoading(true);
    try {
      await api.register(form.username.trim(), form.email.trim(), form.password);
      const data = await api.login(form.username.trim(), form.password);
      login(data.access_token);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  return (
    <main className="auth-page">
      <section className="card auth-card" aria-labelledby="register-title">
        <div className="card-body">
          <div className="auth-toolbar">
            <ThemeSwitcher compact />
            <button type="button" className="icon-btn" onClick={() => i18n.changeLanguage(lang === 'ru' ? 'en' : 'ru')} title={t('common.switchLanguage')} aria-label={t('common.switchLanguage')}>
              <Languages className="icon" aria-hidden="true" />
            </button>
          </div>
          <div className="auth-brand">
            <img className="auth-logo" src="/logo.png" alt="" />
            <div className="auth-kicker"><CloudCog className="icon d-inline me-2" aria-hidden="true" />Geeklab Cloud</div>
            <h1 id="register-title" className="auth-title">{t('register.title')}</h1>
          </div>
          {error && <div className="alert alert-danger py-2 small" role="alert">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="mb-3"><label className="form-label" htmlFor="register-username">{t('register.username')}</label><input id="register-username" className="form-control" value={form.username} onChange={set('username')} autoComplete="username" autoFocus required /></div>
            <div className="mb-3"><label className="form-label" htmlFor="register-email">{t('register.email')}</label><input id="register-email" className="form-control" type="email" value={form.email} onChange={set('email')} autoComplete="email" required /></div>
            <div className="mb-3"><label className="form-label" htmlFor="register-password">{t('register.password')}</label><input id="register-password" className="form-control" type="password" value={form.password} onChange={set('password')} autoComplete="new-password" required minLength={6} /></div>
            <div className="mb-3"><label className="form-label" htmlFor="register-confirm">{t('register.confirm')}</label><input id="register-confirm" className="form-control" type="password" value={form.confirm} onChange={set('confirm')} autoComplete="new-password" required /></div>
            <button className="btn btn-primary w-100" type="submit" disabled={loading}>{loading && <LoaderCircle className="icon spin" aria-hidden="true" />}{loading ? t('register.submitting') : t('register.submit')}</button>
          </form>

          {botUsername && (
            <>
              <div className="auth-divider"><span>{t('login.or')}</span></div>
              <div className="auth-provider">
                <MessagesSquare className="icon-lg text-primary mb-2" aria-hidden="true" />
                <p className="mb-1 fw-semibold small">{t('register.maxTitle')}</p>
                <p className="mb-0 small text-muted">{t('register.maxHint', { bot: botUsername })}</p>
              </div>
            </>
          )}

          <div className="auth-divider" aria-hidden="true" />
          <p className="text-center mb-0 small">{t('register.haveAccount')}{' '}<Link to="/login">{t('register.login')}</Link></p>
        </div>
      </section>
    </main>
  );
}
